<script setup lang="ts">
import {PropType, ref} from "vue";
import {ICellRendererParams} from "ag-grid-community";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import Translator from "@/utils/common/Translator.ts";
import {Dialog, QInput} from "quasar";
import QuestionListIcon from "@/assets/widgetIcons/QuestionListIcon.vue";
import QuestionListDialog from "@/components/dialogs/QuestionListDialog.vue";
import EditScreenGridHandler from "@/utils/common/EditScreenGridHandler.ts";
import FieldType from "@/utils/constants/FieldType.ts";
import DialogHandler, {CommonDialogAction} from "@/utils/forms/DialogHandler.ts";
import {QuestionFieldsPresenter} from "@/utils/presenters/QuestionFieldsPresenter.ts";
import ClientCache from "@/utils/login/ClientCache.ts";
import {StringUtils} from "@/utils/common/StringUtils.ts";
import NamaURLS from "@/utils/constants/NamaURLS.ts";
import CommonConstants from "@/utils/constants/CommonConstants.ts";
import {WidgetsPresenter} from "@/utils/presenters/interfaces.ts";
import ItemSerialNumberUtil from "@/utils/common/ItemSerialNumberUtil.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import QuestionField = namaTypes.QuestionField;
import FieldMetaData = namaTypes.FieldMetaData;
import DTOFilledQuestionField = namaTypes.DTOFilledQuestionField;
import SearchForSerialRequest = namaTypes.SearchForSerialRequest;
import DTOBasicSCDocumentLine = namaTypes.DTOBasicSCDocumentLine;

const props = defineProps({
  params: {
    type: Object as PropType<ICellRendererParams>,
    required: true
  }
});

const handler: EditScreenGridHandler = props.params["handler"]();
const fieldValue = ref<string>(props.params.value ?? '');
const inputRef = ref<QInput | null>(null);
let columnMetadata = props.params['columnMetadata'] ?? {};
const fieldId: string = columnMetadata.fieldId ?? '';
const entityType: string | undefined = handler.presenter.entityType;
const isColumnDisabled: boolean = handler.presenter.isColumnDisabled(props.params.data.id, handler.gridId, fieldId);
let columnId = StringUtils.substringAfterLast(fieldId, ".")
let questionFieldsPresenter: QuestionFieldsPresenter = new QuestionFieldsPresenter(entityType);

let extraButton: CommonDialogAction = new CommonDialogAction("searchForItemSerialsInDB", async () => {
  let line: DTOBasicSCDocumentLine = props.params.data as DTOBasicSCDocumentLine;
  let request: SearchForSerialRequest = {fieldId, entityType, line, propertyId: columnId};
  let response = await NamaCommon.namaFetch(NamaURLS.SEARCH_FOR_SERIAL, {data: NamaCommon.stringify(request)})
  if (response && response.list) {
    await QuestionFieldsPresenter.setQuestionListValue(questionFieldsPresenter.fetchRecord(), columnId + '.' + QuestionFieldsPresenter.detailId, response.list)
  }
});

function setListValue(serials?: string) {
  if (ObjectChecker.isNotNullOrUndefined(serials)) {
    if (props.params && props.params.setValue) {
      props.params.setValue(serials);
      fieldValue.value = serials!;
    }
  }
}

async function showTextAreaSerialDialog() {
  let fieldMetaData: FieldMetaData = columnMetadata ? NamaCommon.cloneObject(columnMetadata) : {};
  fieldMetaData.fieldType = FieldType.BigText;
  let questionField: QuestionField = {id: columnId, metaData: fieldMetaData};
  let defaultValue: DTOFilledQuestionField = {
    id: columnId,
    value: StringUtils.toCSVLineWithSep("\n", ItemSerialNumberUtil.unZipSerials(fieldValue.value)),
    objValue: fieldValue.value
  }
  await questionFieldsPresenter.edit([questionField], [defaultValue], []);
  let okBtn: CommonDialogAction = CommonDialogAction.createOkDialogAction(async (object: Record<string, any>) => {
    if (object[columnId]) {
      let text: string = object[columnId] ?? '';
      text = ItemSerialNumberUtil.checkDuplicatesSerials(text);
      text = ItemSerialNumberUtil.zipSerialRange(StringUtils.csvLineToList(text, "\n"));
      setListValue(text)
    }
  });
  let dialogBox = new DialogHandler([okBtn, CommonDialogAction.createCancelDialogAction(() => {  })], Translator.translateId(fieldId), true);
  await dialogBox.createPresenter(questionFieldsPresenter as WidgetsPresenter);
  await dialogBox.runDialogBox();
}

async function showGridSerialDialog(e: MouseEvent) {
  let fieldMetaData: FieldMetaData = columnMetadata ? NamaCommon.cloneObject(columnMetadata) : {};
  fieldMetaData.fieldType = FieldType.Text;
  let defaultValue: DTOFilledQuestionField = {
    id: columnId,
    value: StringUtils.toCSVLineWithSep(CommonConstants.LIST_PARAMS_SEP, ItemSerialNumberUtil.unZipSerials(fieldValue.value)),
    objValue: fieldValue.value
  }
  let questionField: QuestionField = {id: columnId, metaData: fieldMetaData, list: true};
  await questionFieldsPresenter.edit([questionField], [defaultValue], []);
  Dialog.create({
    component: QuestionListDialog,
    componentProps: {
      presenter: questionFieldsPresenter,
      questionField: questionField,
      extraButtons: [extraButton],
      clickEvent: e
    }
  }).onOk(() => {
    let filledQuestionFields: DTOFilledQuestionField[] = questionFieldsPresenter.getQuestionFields()
    if (filledQuestionFields.length) {
      let line = filledQuestionFields.find(l => l.id === columnId);
      if (line) {
        let serials: string = ItemSerialNumberUtil.checkDuplicatesSerials(line.value ?? '');
        serials = ItemSerialNumberUtil.zipSerialRange(StringUtils.csvLineToList(serials, CommonConstants.LIST_PARAMS_SEP));
        setListValue(serials);
      }
    }
  });
}

async function onClick(e: MouseEvent) {
  if (isColumnDisabled)
    return;
  let ctrlDown: boolean = e.altKey || e.ctrlKey;
  if (ClientCache.getCustomGUISettings()?.tooltipsAndReportsAndRevise?.alwaysUseTextAreaInSerialPopup)
    ctrlDown = !ctrlDown;
  if (ctrlDown) {
    await showTextAreaSerialDialog();
  } else {
    await showGridSerialDialog(e);
  }
}
</script>

<template>
  <div class="tw-whitespace-pre-wrap">
    <q-input ref="inputRef" dense hide-bottom-space v-model="fieldValue" autocomplete="off" readonly
             class="ag-custom-component-popup nama-field-input-disabled nama-customize-dense-input nama-focused-input nama-dark-popup"
             input-class="tw-bg-transparent focus:tw-ring-0 focus:tw-shadow-none focus:tw-border-none dark:tw-text-namaFontColor-input-dark dark:tw-placeholder-gray-300">
      <template v-slot:append>
        <div class="tw-pl-2 tw-pr-1 tw-flex">
          <q-avatar square @click.stop.prevent="onClick" :title="Translator.translateId('select')"
                    :class="{'nama-avatar-hover tw-cursor-pointer':!isColumnDisabled}">
            <QuestionListIcon class="tw-w-[18px] tw-h-[18px] nama-widget-icon" :class="{'nama-widget-icon-hover':!isColumnDisabled}"/>
          </q-avatar>
        </div>
      </template>
    </q-input>
  </div>
</template>

<style scoped>

</style>
